if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      // Shared cache of fetched product HTML
      static fetchCache = new Map();
      static pendingFetches = new Map();

      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]') || this.querySelector('.quick-add-modal__content-info') || this;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (this.modalContent) this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        this.openedBy = opener;
        const productUrl = opener.getAttribute('data-product-url');
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]') || this.querySelector('.quick-add-modal__content-info') || this;

        // Immediately show skeleton and open modal if not cached
        if (!QuickAddModal.fetchCache.has(productUrl)) {
          this.modalContent.innerHTML = this.buildSkeleton();
        }

        super.show(opener);

        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        this.renderFromCache(productUrl, opener);
      }

      buildSkeleton() {
        return `
          <div class="quick-add-skeleton" aria-hidden="true">
            <div class="quick-add-skeleton__img"></div>
            <div class="quick-add-skeleton__body">
              <div class="quick-add-skeleton__line quick-add-skeleton__line--title"></div>
              <div class="quick-add-skeleton__line quick-add-skeleton__line--price"></div>
              <div class="quick-add-skeleton__swatches">
                <div class="quick-add-skeleton__swatch"></div>
                <div class="quick-add-skeleton__swatch"></div>
                <div class="quick-add-skeleton__swatch"></div>
                <div class="quick-add-skeleton__swatch"></div>
              </div>
              <div class="quick-add-skeleton__line quick-add-skeleton__line--btn"></div>
            </div>
          </div>`;
      }

      renderFromCache(productUrl, opener) {
        if (QuickAddModal.fetchCache.has(productUrl)) {
          this.renderProduct(QuickAddModal.fetchCache.get(productUrl), opener);
          return;
        }

        if (QuickAddModal.pendingFetches.has(productUrl)) {
          QuickAddModal.pendingFetches.get(productUrl)
            .then((html) => this.renderProduct(html, opener))
            .catch(() => this.resetOpener(opener));
          return;
        }

        const fetchPromise = fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            QuickAddModal.fetchCache.set(productUrl, responseText);
            QuickAddModal.pendingFetches.delete(productUrl);
            return responseText;
          });

        QuickAddModal.pendingFetches.set(productUrl, fetchPromise);

        fetchPromise
          .then((html) => this.renderProduct(html, opener))
          .catch(() => this.resetOpener(opener));
      }

      renderProduct(responseText, opener) {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const productElement = responseHTML.querySelector('product-info');
        if (!productElement) {
          this.resetOpener(opener);
          return;
        }

        this.preprocessHTML(productElement);

        // Preload images immediately
        productElement.querySelectorAll('img').forEach((img) => {
          img.setAttribute('loading', 'eager');
          img.setAttribute('fetchpriority', 'high');
          img.setAttribute('decoding', 'sync');
        });

        HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        this.resetOpener(opener);
      }

      resetOpener(opener) {
        if (!opener) return;
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('loading');
        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

// ────────────────────────────────────────────────────────────────────────────
// IntersectionObserver: pre-fetch quick-add HTML for product cards in view
// ────────────────────────────────────────────────────────────────────────────
(function initQuickAddPrefetch() {
  if (typeof IntersectionObserver === 'undefined') return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const opener = entry.target.querySelector('[data-product-url]');
        if (!opener) return;

        const url = opener.getAttribute('data-product-url');
        if (!url || QuickAddModal.fetchCache?.has(url) || QuickAddModal.pendingFetches?.has(url)) return;

        observer.unobserve(entry.target);

        const fetchPromise = fetch(url)
          .then((r) => r.text())
          .then((html) => {
            QuickAddModal.fetchCache?.set(url, html);
            QuickAddModal.pendingFetches?.delete(url);
            return html;
          })
          .catch(() => {
            QuickAddModal.pendingFetches?.delete(url);
          });

        QuickAddModal.pendingFetches?.set(url, fetchPromise);
      });
    },
    { rootMargin: '300px' }
  );

  function observeCards() {
    document.querySelectorAll('.product-card-wrapper, .card-wrapper').forEach((card) => {
      if (!card.dataset.prefetchObserved) {
        card.dataset.prefetchObserved = '1';
        observer.observe(card);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCards);
  } else {
    observeCards();
  }

  const mutationObserver = new MutationObserver(observeCards);
  mutationObserver.observe(document.body, { childList: true, subtree: true });
})();

// ────────────────────────────────────────────────────────────────────────────
// Card swatch hover & click → instant card image swap with skeleton (Colors only)
// ────────────────────────────────────────────────────────────────────────────
(function initCardSwatchHover() {
  function getCardImg(card) {
    return card.querySelector('.card-main-img') || card.querySelector('.media img');
  }

  function saveOriginals(img) {
    if (!img.dataset.originalSrc) {
      img.dataset.originalSrc = img.currentSrc || img.src;
    }
    if (!img.dataset.originalSrcset && img.srcset) {
      img.dataset.originalSrcset = img.srcset;
    }
  }

  function switchCardImage(card, img, newSrc) {
    card.classList.add('swatch-hover-active');
    card.classList.add('is-loading-img');

    const temp = new Image();
    temp.src = newSrc;
    temp.onload = () => {
      img.srcset = newSrc;
      img.src = newSrc;
      card.classList.remove('is-loading-img');
    };
    temp.onerror = () => {
      card.classList.remove('is-loading-img');
    };
    if (temp.complete) {
      img.srcset = newSrc;
      img.src = newSrc;
      card.classList.remove('is-loading-img');
    }
  }

  document.addEventListener('mouseover', (e) => {
    const swatch = e.target.closest('.card-swatch');
    if (!swatch) return;

    const swatchImg = swatch.getAttribute('data-swatch-img');
    if (!swatchImg) return;

    const card = swatch.closest('.card-wrapper') || swatch.closest('.product-card-wrapper');
    if (!card) return;

    const img = getCardImg(card);
    if (!img) return;

    saveOriginals(img);
    switchCardImage(card, img, swatchImg);
  });

  document.addEventListener('mouseout', (e) => {
    const swatch = e.target.closest('.card-swatch');
    if (!swatch) return;

    const swatchesContainer = swatch.closest('.card-product__swatches');
    if (swatchesContainer && swatchesContainer.contains(e.relatedTarget)) return;

    const card = swatch.closest('.card-wrapper') || swatch.closest('.product-card-wrapper');
    if (!card) return;

    const activeSwatch = card.querySelector('.card-swatch.is-active');
    const img = getCardImg(card);
    if (!img) return;

    if (activeSwatch && activeSwatch.getAttribute('data-swatch-img')) {
      const activeImg = activeSwatch.getAttribute('data-swatch-img');
      switchCardImage(card, img, activeImg);
    } else {
      card.classList.remove('is-loading-img');
      card.classList.remove('swatch-hover-active');
      if (img.dataset.originalSrcset) {
        img.srcset = img.dataset.originalSrcset;
      }
      if (img.dataset.originalSrc) {
        img.src = img.dataset.originalSrc;
      }
    }
  });

  document.addEventListener('click', (e) => {
    const swatch = e.target.closest('.card-swatch');
    if (!swatch) return;

    e.preventDefault();
    e.stopPropagation();

    const card = swatch.closest('.card-wrapper') || swatch.closest('.product-card-wrapper');
    if (!card) return;

    const allSwatches = card.querySelectorAll('.card-swatch');
    allSwatches.forEach((s) => s.classList.remove('is-active'));
    swatch.classList.add('is-active');

    const swatchImg = swatch.getAttribute('data-swatch-img');
    const img = getCardImg(card);
    if (img && swatchImg) {
      img.dataset.originalSrc = swatchImg;
      img.dataset.originalSrcset = swatchImg;
      switchCardImage(card, img, swatchImg);
    }

    const variantUrl = swatch.getAttribute('data-variant-url');
    if (variantUrl) {
      card.querySelectorAll('a.full-unstyled-link, .card__heading a, a.card__media-link').forEach((a) => {
        a.href = variantUrl;
      });
    }
  });
})();






